import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import DateInput from "@_components/DateInput/DateInput";
import {
  getUserDataFromJwt,
  isComponentDisabled,
  isNull,
  catchError,
} from "@_utils/helpers";
import styles from "./ClaimPopup.module.css";
import Switch from "@mui/material/Switch";
import { caseActions } from "@_actions/case.actions";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import CountryAutocompleteList from "@_configs/CountryAutocompleteList.config";
import TaxTypes from "@_configs/TaxType.config";
import CurrencyList from "@_configs/CurrencyList.config";
import { caseConstants } from "@_constants/case.constants";
import { FormControlLabel } from "@mui/material";
import Scrollbar from "react-perfect-scrollbar-z";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";

const componetTag = "claim_popup";

const getInitialInputs = (data) => ({
  currency: data.claim_details?.currency,
  indirect_tax_type: data.claim_details?.indirect_tax_type || "",
  indirect_tax_percentage: data.claim_details?.indirect_tax_percentage,
  country: data.country,
  settlement_offer_date: data.claim_details?.settlement_offer_date || "",
  settlement_offer_percentage:
    data.claim_details?.settlement_offer_percentage || "",
  invoice_template_id: data.invoice_template_id || "",
  vat_id_required: data.vat_id_required || false,
  allow_prepayment_invoicing: data.allow_prepayment_invoicing || false,
  custom_fields: data.billing_address?.custom_fields || [],
});

const getInitialFindings = (data) => data.findings || [];

const getInitialAdditionalFees = (data) =>
  data.claim_details?.additional_fees || [];

function ClaimPopup(props) {
  const dispatch = useDispatch();
  const jwtData = getUserDataFromJwt();
  const { data, caseId, close } = props;
  const inputDisabled = isComponentDisabled(componetTag, data.state);
  const invoiceReducer = useSelector((state) => state.invoiceTemplateReducer);
  const invoiceTemplates = invoiceReducer.templates;
  const caseReducer = useSelector((state) => state.caseReducer);

  const [inputs, setInputs] = useState(getInitialInputs(data));
  const [findings, setFindings] = useState(getInitialFindings(data));
  const [additionalFees, setAdditionalFees] = useState(
    getInitialAdditionalFees(data),
  );
  const [isClicked, setIsClicked] = useState(false);
  const [awaitingClaimWrites, setAwaitingClaimWrites] = useState(false);
  const [customFieldsTouched, setCustomFieldsTouched] = useState(false);
  const { snackbarProps, showSnackbarError } = useSnackbar();
  const shouldFocusNewCustomField = useRef(false);
  const newCustomFieldInputRef = useRef(null);

  const isTaxable =
    !!inputs.indirect_tax_type &&
    inputs.indirect_tax_type !== caseConstants.CASE_TAX_TYPE_NO_INDIRECT_TAX &&
    inputs.indirect_tax_type !== caseConstants.CASE_TAX_TYPE_REVERSE_CHARGE;

  const filteredInvoiceTemplates = invoiceTemplates.filter(
    (invoice) => invoice.tax_type === inputs.indirect_tax_type,
  );

  useEffect(() => {
    setFindings(getInitialFindings(data));
    setAdditionalFees(getInitialAdditionalFees(data));
    setInputs(getInitialInputs(data));
  }, [data]);

  useEffect(() => {
    if (!awaitingClaimWrites) return;
    const clearStatusMessages = () => {
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_COUNTRY"));
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_CLAIM_DETAILS"));
      dispatch(caseActions.clearStateMessage("CASE_ADD_ADDITIONAL_FEES"));
      dispatch(
        caseActions.clearStateMessage("CASE_UPDATE_BILLING_CUSTOM_FIELDS"),
      );
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_FINDING_PRICE"));
    };

    const nonFindingStatuses = [
      caseReducer.status.CASE_UPDATE_COUNTRY,
      caseReducer.status.CASE_UPDATE_CLAIM_DETAILS,
      caseReducer.status.CASE_ADD_ADDITIONAL_FEES,
      caseReducer.status.CASE_UPDATE_BILLING_CUSTOM_FIELDS,
    ];

    const statuses = [
      ...nonFindingStatuses,
      caseReducer.status.CASE_UPDATE_FINDING_PRICE,
    ];

    const hasFindingsToUpdate = (findings || []).length > 0;
    const statusesToCheckForError = hasFindingsToUpdate
      ? statuses
      : nonFindingStatuses;
    const findingPriceStatusCompleted =
      caseReducer.status.CASE_UPDATE_FINDING_PRICE === "success" ||
      !hasFindingsToUpdate;

    const hasErrorStatus = statusesToCheckForError.find((status) =>
      catchError(status),
    );
    if (hasErrorStatus) {
      setAwaitingClaimWrites(false);
      clearStatusMessages();
      showSnackbarError(hasErrorStatus);
      return;
    } else if (
      ((data.state === "PAID" ||
        data.state === caseConstants.CASE_STATE_CANCELLED) &&
        caseReducer.status.CASE_UPDATE_CLAIM_DETAILS === "success") ||
      (nonFindingStatuses.every((status) => status === "success") &&
        findingPriceStatusCompleted)
    ) {
      clearStatusMessages();
      dispatch(caseActions.getCaseWithChargeDetails(caseId, data.overview));
      setAwaitingClaimWrites(false);
      close();
    }
    // eslint-disable-next-line
  }, [
    awaitingClaimWrites,
    caseReducer.status,
    dispatch,
    caseId,
    data.overview,
    close,
    showSnackbarError,
  ]);

  const checkInputsValidity = useCallback(() => {
    if (
      (!isNull(inputs.settlement_offer_percentage) &&
        inputs.settlement_offer_percentage < 0) ||
      (inputs.settlement_offer_percentage <= 0 &&
        inputs.settlement_offer_date) ||
      (inputs.settlement_offer_percentage > 0 && !inputs.settlement_offer_date)
    ) {
      showSnackbarError(
        "Please provide a discount  percentage and date. Percentage must be larger than 0 and date must be provided if percentage is provided.",
      );
      return false;
    }
    if (
      isTaxable &&
      (isNull(inputs.indirect_tax_percentage) ||
        inputs.indirect_tax_percentage <= 0)
    ) {
      showSnackbarError("VAT Percentage must be larger than 0");
      return false;
    }
    if (isNull(inputs.country)) {
      showSnackbarError("Country must be provided");
      return false;
    }
    if (isNull(inputs.currency)) {
      showSnackbarError("Currency must be provided");
      return false;
    }
    if (
      inputs.settlement_offer_date &&
      moment(inputs.settlement_offer_date).format("DD/MM/yyyy") ===
        "Invalid date"
    ) {
      showSnackbarError("Invalid settlement offer date!");
      return false;
    }

    if (inputs.settlement_offer_percentage < 0) {
      showSnackbarError(
        "Settlement offer percentage can't be a negative value",
      );
      return false;
    }

    return true;
  }, [inputs, showSnackbarError, isTaxable]);

  const checkFindingsPriceValidity = useCallback(() => {
    const hasInvalidFindingPrice = findings?.some(
      (finding) => finding.price <= 0 || !finding.price,
    );
    if (hasInvalidFindingPrice) {
      showSnackbarError("Finding price must be larger than 0");
      return false;
    }
    return true;
  }, [findings, showSnackbarError]);

  const checkAdditionalFeesValidity = useCallback(() => {
    const hasMissingFeeName = additionalFees.some(
      (fee) => !fee.name || fee.name.trim() === "",
    );
    if (hasMissingFeeName) return false;

    const hasInvalidFeePercentage = additionalFees.some(
      (fee) => fee.percentage < 0 || !fee.percentage,
    );
    if (hasInvalidFeePercentage) return false;
    return true;
  }, [additionalFees]);

  function actionClicked() {
    //TODO: come up with better validation than if checks

    // in case the state of the case is paid only the invoice
    // template can be changed, so only update that
    if (data.state === "PAID") {
      if (inputs.indirect_tax_percentage === null)
        delete inputs.indirect_tax_percentage;
      dispatch(caseActions.updateCaseClaimDetails(caseId, inputs));
      setAwaitingClaimWrites(true);
    } else {
      setIsClicked(true);
      if (
        inputs.indirect_tax_type ===
          caseConstants.CASE_TAX_TYPE_NO_INDIRECT_TAX ||
        inputs.indirect_tax_type === caseConstants.CASE_TAX_TYPE_REVERSE_CHARGE
      ) {
        delete inputs.indirect_tax_percentage;
      }
      // non paid state-> update everything
      if (!checkInputsValidity()) return;
      if (!checkFindingsPriceValidity()) return;
      if (!checkAdditionalFeesValidity()) {
        showSnackbarError(
          "Please make sure all additional fees have a name and a percentage value larger than 0",
        );
        return;
      }

      const hasMissingCustomFieldLabel = (inputs.custom_fields || []).some(
        (field) => !field.label || field.label.trim() === "",
      );
      if (hasMissingCustomFieldLabel) {
        setCustomFieldsTouched(true);
        showSnackbarError(
          "Please provide a label for all custom invoice fields",
        );
        return;
      }

      /**
       * Filters out null and empty string values from the inputs object.
       * Creates a new object containing only the key-value pairs where the value
       * is neither null nor an empty string.
       */
      const { custom_fields, ...claimInputs } = inputs;
      const reducedInputs = Object.fromEntries(
        Object.entries(claimInputs).filter(([_, v]) => v !== null && v !== ""),
      );
      dispatch(caseActions.updateCaseClaimDetails(caseId, reducedInputs));

      if (
        data.state === caseConstants.CASE_STATE_ACTIVE ||
        data.state === caseConstants.CASE_STATE_STANDBY
      ) {
        dispatch(caseActions.updateCaseCountry(caseId, reducedInputs));

        if (additionalFees.length > 0) {
          dispatch(caseActions.addCaseAdditionalFees(caseId, additionalFees));
        } else {
          dispatch(caseActions.addCaseAdditionalFees(caseId, []));
        }

        findings &&
          findings.forEach((finding) => {
            const findingData = {
              ...finding,
              price: parseFloat(finding.price),
            };
            dispatch(caseActions.updateCaseFindingPrice(caseId, findingData));
          });
      }
      dispatch(
        caseActions.updateCaseBillingCustomFields(caseId, custom_fields || []),
      );

      setAwaitingClaimWrites(true);
    }
  }

  function addAdditionalFees() {
    let newAdditionalFee = [...additionalFees];
    const emptyAdditionalFee = {
      name: "",
      percentage: 0,
    };
    newAdditionalFee.unshift(emptyAdditionalFee);
    setAdditionalFees(newAdditionalFee);
  }

  function handleDeleteAdditionalFee(e, index) {
    let newAdditionalFee = [...additionalFees];
    newAdditionalFee.splice(index, 1);
    setAdditionalFees(newAdditionalFee);
  }

  const MAX_CUSTOM_INVOICE_FIELDS = 2;

  useEffect(() => {
    if (!shouldFocusNewCustomField.current) return;
    shouldFocusNewCustomField.current = false;
    if (newCustomFieldInputRef.current) newCustomFieldInputRef.current.focus();
  }, [inputs.custom_fields]);

  function addCustomInvoiceField() {
    setCustomFieldsTouched(false);
    shouldFocusNewCustomField.current = true;
    setInputs((prev) => ({
      ...prev,
      custom_fields: [
        ...(prev.custom_fields || []),
        { label: "", required: false },
      ],
    }));
  }

  function handleDeleteCustomInvoiceField(index) {
    setInputs((prev) => ({
      ...prev,
      custom_fields: (prev.custom_fields || []).filter((_, i) => i !== index),
    }));
  }

  function handleCustomInvoiceFieldChange(index, field, value) {
    setInputs((prev) => {
      const updated = [...(prev.custom_fields || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, custom_fields: updated };
    });
  }

  function isValidWithOneDecimal(value) {
    let normalized = value.replace(",", ".");
    const [, decimalPart] = normalized.split(".");
    if (decimalPart && decimalPart.length > 1) {
      return false;
    }
    return true;
  }

  function handleInputChange(e) {
    const { id, name, value: rawValue } = e.target;
    let value = rawValue;

    // percentage fields
    if (
      id === "indirect_tax_percentage" ||
      id === "settlement_offer_percentage"
    ) {
      if (value === "") {
        // allow empty field so user can type
        setInputs((inputs) => ({
          ...inputs,
          [id]: value,
        }));
        return;
      }

      if (!isValidWithOneDecimal(value)) {
        return; // invalid input, stop updating
      }

      value = parseFloat(value);

      // cap settlement offer percentage at 100
      if (id === "settlement_offer_percentage" && value > 100) {
        value = 100;
      }
    }
    if (name === "indirect_tax_type" || id === "indirect_tax_type")
      setInputs((inputs) => ({
        ...inputs,
        invoice_template_id: "",
        allow_prepayment_invoicing: false,
      }));
    if (name === "invoice_template_id" && !value) {
      setInputs((inputs) => ({
        ...inputs,
        allow_prepayment_invoicing: false,
      }));
    }
    setInputs((inputs) => ({
      ...inputs,
      [id || name]: value,
    }));
  }

  function handleDateChange(date) {
    setInputs({ ...inputs, settlement_offer_date: date });
  }

  function handleFindingPriceInputChange(e, index) {
    let newFindings = [...findings];
    newFindings[index].price = parseFloat(e.target.value);
    setFindings(newFindings);
  }

  function handleAdditionalFeesInputChange(e, index) {
    let newAdditionalFee = [...additionalFees];
    const { name, value: rawValue } = e.target;
    let value = rawValue;

    if (!isValidWithOneDecimal(value)) {
      return; // invalid input, stop updating
    }
    newAdditionalFee[index][name] =
      name === "percentage" ? parseFloat(e.target.value) : e.target.value;
    setAdditionalFees(newAdditionalFee);
  }

  const handleCancel = () => {
    setFindings(getInitialFindings(data));
    setAdditionalFees(getInitialAdditionalFees(data));
    setInputs(getInitialInputs(data));
    setCustomFieldsTouched(false);
    props.close();
  };

  return (
    <DialogPopup
      title="Licensing offer"
      open={props.open}
      onClose={() => handleCancel()}
    >
      <Scrollbar maxHeight="74vh" options={{ suppressScrollX: true }}>
        <div className={styles.content_container}>
          <TextfieldInput
            select
            label="Currency"
            id="currency"
            name="currency"
            value={inputs.currency}
            onChange={handleInputChange}
            disabled={inputDisabled}
            error={isClicked && isNull(inputs.currency)}
            required
          >
            {CurrencyList.map((currency) => (
              <MenuItem key={currency.id} value={currency.id}>
                {`${currency.label} (${currency.id})`}
              </MenuItem>
            ))}
          </TextfieldInput>

          <div className={styles.findings_container}>
            {findings &&
              findings.map((finding, index) => (
                <div className={styles.finding_container} key={index}>
                  <div className={styles.finging_image_container}>
                    <img
                      className={styles.finging_image}
                      src={finding.original_image.original_image_url}
                      alt={"Finding result"}
                    />
                  </div>
                  <div className={styles.finging_details_container}>
                    <p className={styles.finding_id}>
                      {finding.original_image.name}
                    </p>
                  </div>
                  <div className={styles.finging_price_container}>
                    {finding.license && finding.license.state === "VALID" ? (
                      <p className={styles.valid_note}>
                        Valid license provided
                      </p>
                    ) : (
                      <TextfieldInput
                        label="License Fee"
                        name={"finding" + index}
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        id={"finding" + index}
                        defaultValue={finding.price}
                        onChange={(e) =>
                          handleFindingPriceInputChange(e, index)
                        }
                        disabled={inputDisabled}
                        required
                        error={
                          isClicked && (finding.price <= 0 || !finding.price)
                        }
                      />
                    )}
                  </div>
                </div>
              ))}
            {(!findings || findings.length <= 0) && (
              <div className={styles.no_findings}>
                Please add image uses and evidence in order to set up your
                licensing offer.
              </div>
            )}
          </div>

          {!inputDisabled && (
            <>
              <hr className={styles.claim_hr} />
              <div className={styles.add_fee_container}>
                <Button
                  className={styles.btn_confirm_small}
                  size="small"
                  onClick={addAdditionalFees}
                  disabled={inputDisabled}
                >
                  Add Additional Fees
                </Button>
              </div>
            </>
          )}

          <div className={styles.additional_fees_container}>
            {additionalFees.map((fee, index) => (
              <div className={styles.additional_fee_container} key={index}>
                <TextfieldInput
                  className={styles.first_input}
                  label="Additional Fee Name"
                  id={"name" + index}
                  name={"name"}
                  value={fee.name}
                  onChange={(e) => handleAdditionalFeesInputChange(e, index)}
                  disabled={inputDisabled}
                  error={!fee.name || fee.name.trim() === ""}
                />
                <TextfieldInput
                  label="Percentage %"
                  id={"percentage" + index}
                  type="number"
                  InputProps={{ inputProps: { min: 0 } }}
                  name={"percentage"}
                  value={fee.percentage || ""}
                  onChange={(e) => handleAdditionalFeesInputChange(e, index)}
                  disabled={inputDisabled}
                  error={fee.percentage < 0 || !fee.percentage}
                />
                {!inputDisabled && (
                  <div className={styles.field_row_delete_container}>
                    <DeleteIcon
                      className={styles.delete_icon}
                      onClick={(e) => handleDeleteAdditionalFee(e, index)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <hr className={styles.claim_hr} />
          <div className={styles.other_fees_container}>
            <div className={styles.settelment_container}>
              <TextfieldInput
                className={styles.first_input}
                label="Discount %"
                id="settlement_offer_percentage"
                name="settlement_offer_percentage"
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                value={inputs.settlement_offer_percentage || ""}
                onChange={handleInputChange}
                disabled={
                  inputDisabled &&
                  data.state !== caseConstants.CASE_STATE_CANCELLED
                }
                error={
                  (!isNull(inputs.settlement_offer_percentage) &&
                    inputs.settlement_offer_percentage < 0) ||
                  (inputs.settlement_offer_percentage <= 0 &&
                    inputs.settlement_offer_date)
                }
              />
              <DateInput
                label="Discount Deadline"
                id="settlement_offer_date"
                name="settlement_offer_date"
                value={inputs.settlement_offer_date}
                onChange={(date) => handleDateChange(date)}
                error={
                  inputs.settlement_offer_percentage > 0 &&
                  !inputs.settlement_offer_date
                }
                disabled={
                  inputDisabled &&
                  data.state !== caseConstants.CASE_STATE_CANCELLED
                }
                disablePast
              />
            </div>
            <div className={styles.vat_container}>
              <TextfieldInput
                className={styles.input_small}
                select
                label="Country"
                id="country"
                name="country"
                value={inputs.country}
                onChange={handleInputChange}
                disabled={inputDisabled}
                error={isClicked && isNull(inputs.country)}
                required
              >
                {CountryAutocompleteList.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {`${country.label} (${country.id})`}
                  </MenuItem>
                ))}
              </TextfieldInput>
              <TextfieldInput
                className={styles.input_small}
                select
                label="Tax"
                id="indirect_tax_type"
                name="indirect_tax_type"
                value={inputs.indirect_tax_type}
                onChange={handleInputChange}
                disabled={inputDisabled}
                error={isClicked && isNull(inputs.indirect_tax_type)}
                required
              >
                {TaxTypes.map((TaxType) => (
                  <MenuItem key={TaxType.id} value={TaxType.id}>
                    {`${TaxType.label} `}
                  </MenuItem>
                ))}
              </TextfieldInput>

              <TextfieldInput
                className={styles.input_small}
                label="Tax Percentage %"
                id="indirect_tax_percentage"
                name="indirect_tax_percentage"
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                value={inputs.indirect_tax_percentage || ""}
                onChange={handleInputChange}
                disabled={inputDisabled || !isTaxable}
                style={!isTaxable ? { visibility: "hidden" } : {}}
                error={
                  (isClicked && isNull(inputs.indirect_tax_percentage)) ||
                  (isClicked && inputs.indirect_tax_percentage <= 0)
                }
                required
              />
            </div>
            {jwtData.subscription.toLowerCase() !== "lite" && (
              <>
                <div className={styles.vat_container}>
                  <TextfieldInput
                    select
                    label="Invoice Template"
                    id="invoice_template_id"
                    name="invoice_template_id"
                    value={inputs.invoice_template_id}
                    onChange={handleInputChange}
                    disabled={!inputs.indirect_tax_type}
                  >
                    <MenuItem value="">None</MenuItem>
                    {filteredInvoiceTemplates.map((invoice) => (
                      <MenuItem key={invoice.id} value={invoice.id}>
                        {`${invoice.name}`}
                      </MenuItem>
                    ))}
                  </TextfieldInput>
                  <FormControlLabel
                    required
                    control={
                      <Switch
                        color="primary"
                        className={styles.taxable_switch}
                        checked={inputs.vat_id_required}
                        onChange={(e) =>
                          setInputs((inputs) => ({
                            ...inputs,
                            vat_id_required: !inputs.vat_id_required,
                          }))
                        }
                        id={"vat_id_required"}
                        name="vat_id_required"
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label="Website owner's VAT ID required"
                  />
                </div>
                <FormControlLabel
                  style={!inputs.invoice_template_id ? { display: "none" } : {}}
                  required
                  control={
                    <Switch
                      color="primary"
                      className={styles.taxable_switch}
                      checked={
                        inputs.allow_prepayment_invoicing &&
                        inputs.invoice_template_id
                      }
                      onChange={(e) =>
                        setInputs((inputs) => ({
                          ...inputs,
                          allow_prepayment_invoicing:
                            !inputs.allow_prepayment_invoicing,
                        }))
                      }
                      id={"allow_prepayment_invoicing"}
                      name="allow_prepayment_invoicing"
                      inputProps={{ "aria-label": "primary checkbox" }}
                      disabled={!inputs.invoice_template_id}
                    />
                  }
                  label="Allow invoice generation before payment"
                />

                <hr className={styles.claim_hr} />

                <div className={styles.custom_fields_container}>
                  {!inputDisabled && (
                    <div className={styles.add_fee_container}>
                      <Tooltip
                        title={
                          (inputs.custom_fields || []).length >=
                          MAX_CUSTOM_INVOICE_FIELDS
                            ? `Only ${MAX_CUSTOM_INVOICE_FIELDS} custom invoice fields are allowed`
                            : ""
                        }
                      >
                        <span>
                          <Button
                            className={styles.btn_confirm_small}
                            size="small"
                            onClick={addCustomInvoiceField}
                            data-testid="claim-popup-add-custom-invoice-field"
                            disabled={
                              (inputs.custom_fields || []).length >=
                              MAX_CUSTOM_INVOICE_FIELDS
                            }
                          >
                            Add Custom Invoice Field
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  )}
                  {(inputs.custom_fields || []).map((field, index) => (
                    <div className={styles.custom_field_row} key={index}>
                      <TextfieldInput
                        className={styles.first_input}
                        label="Custom Invoice Field Label"
                        id={`custom_field_label_${index}`}
                        inputRef={
                          index === (inputs.custom_fields || []).length - 1
                            ? newCustomFieldInputRef
                            : null
                        }
                        name="label"
                        value={field.label}
                        onChange={(e) =>
                          handleCustomInvoiceFieldChange(
                            index,
                            "label",
                            e.target.value,
                          )
                        }
                        disabled={inputDisabled}
                        error={
                          customFieldsTouched &&
                          (!field.label || field.label.trim() === "")
                        }
                        required
                      />
                      <div className={styles.custom_field_switch_container}>
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              className={styles.taxable_switch}
                              checked={field.required}
                              onChange={() =>
                                handleCustomInvoiceFieldChange(
                                  index,
                                  "required",
                                  !field.required,
                                )
                              }
                              id={`custom_field_required_${index}`}
                              name="required"
                              data-testid={`custom-field-required-${index}`}
                              inputProps={{
                                "aria-label": "required toggle",
                              }}
                              disabled={inputDisabled}
                            />
                          }
                          label="Required"
                        />
                      </div>
                      {!inputDisabled && (
                        <div className={styles.field_row_delete_container}>
                          <DeleteIcon
                            className={styles.delete_icon}
                            data-testid={`delete-custom-field-${index}`}
                            onClick={() =>
                              handleDeleteCustomInvoiceField(index)
                            }
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </Scrollbar>
      <div className="action_container">
        <>
          <Button
            className="btn_cancel"
            onClick={() => handleCancel()}
            data-testid="claim-popup-cancel"
          >
            Cancel
          </Button>
          <Button
            className="btn_confirm"
            onClick={actionClicked}
            data-testid="claim-popup-confirm"
          >
            Confirm
          </Button>
        </>
      </div>
      <AppSnackbar {...snackbarProps} />
    </DialogPopup>
  );
}

export default ClaimPopup;
